import { Component, effect, input } from '@angular/core';
import { FormBuilder, FormGroup, FormArray } from '@angular/forms';
import { DeliveryNoteWithDiscrepancies } from '@app/consignee/components/checkin-notes-articles/checkin-notes-articles.component';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { CustomTooltipComponent } from '../../custom-tooltip/custom-tooltip.component';
import { TranslateService } from '@ngx-translate/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from '@app/shared/material/material.module';

interface LoadCarrier {
    _key: string;
    damaged: boolean;
    description: string;
    name: string;
    notice: string;
    quantity: number;
}

export interface StoredCarrierData {
    [checkinKey: string]: {
        [articleId: string]: {
            [carrierName: string]: number;
        };
    };
}

@Component({
    selector: 'app-load-carrier-form',
    templateUrl: './load-carrier-form.component.html',
    styleUrls: ['./load-carrier-form.component.scss'],
    imports: [MaterialModule, ReactiveFormsModule],
})
export class LoadCarrierFormComponent {
    deliveryNotesSignal =
        input.required<() => DeliveryNoteWithDiscrepancies[]>();
    articleId = input<string>();

    private overlayRef: OverlayRef;
    form: FormGroup;
    carrierTotals: Map<string, number> = new Map();
    private storageKey = 'carrierQuantities';
    initialized = false;
    deliveryNotes?: DeliveryNoteWithDiscrepancies[];

    private get checkinKey(): string {
        return this.deliveryNotes?.[0]?.checkinKey || '';
    }

    constructor(
        private fb: FormBuilder,
        private overlay: Overlay,
        private translateService: TranslateService
    ) {
        this.form = this.fb.group({
            carriers: this.fb.array([]),
        });

        effect(() => {
            this.deliveryNotes = this.deliveryNotesSignal()();
            if (!this.initialized && this.deliveryNotes?.length) {
                this.initialized = true;
                this.initializeComponent();
            } else if (this.initialized && this.deliveryNotes?.length) {
                this.initializeComponent();
            }
        });
    }

    private initializeComponent() {
        this.calculateCarrierTotals();
        const uniqueCarriers = this.getUniqueCarriers();

        while (this.carriers.length) {
            this.carriers.removeAt(0);
        }

        uniqueCarriers.forEach(carrier => {
            this.addCarrierForm(carrier);
        });

        if (this.articleId()) {
            this.loadSavedQuantities();

            this.form.valueChanges.subscribe(() => {
                this.saveQuantities();
            });
        }
    }

    private calculateCarrierTotals() {
        this.carrierTotals.clear();

        if (!this.deliveryNotes?.length) return;

        this.deliveryNotes.forEach(note => {
            note.loadCarriersConsignee?.forEach(carrier => {
                if (carrier.operation === 'adoption') {
                    const currentTotal =
                        this.carrierTotals.get(carrier.name) || 0;
                    this.carrierTotals.set(
                        carrier.name,
                        currentTotal + (carrier.quantity || 0)
                    );
                }
            });
        });
    }

    private loadSavedQuantities() {
        const articleId = this.articleId();
        if (!articleId) return;

        const saved = localStorage.getItem(this.storageKey);
        if (saved) {
            const storedData: StoredCarrierData = JSON.parse(saved);
            const articleData =
                storedData[this.checkinKey]?.[articleId] || {};

            this.carriers.controls.forEach(control => {
                const carrierName = control.get('name')?.value;
                if (carrierName && articleData[carrierName] !== undefined) {
                    control
                        .get('quantity')
                        ?.setValue(articleData[carrierName], {
                            emitEvent: false,
                        });
                }
            });
        }
    }

    private saveQuantities() {
        const articleId = this.articleId();
        if (!this.checkinKey || !articleId) return;

        const saved = localStorage.getItem(this.storageKey);
        const storedData: StoredCarrierData = saved ? JSON.parse(saved) : {};

        if (!storedData[this.checkinKey]) {
            storedData[this.checkinKey] = {};
        }

        const articleData: { [carrierName: string]: number } = {};
        this.carriers.controls.forEach(control => {
            const carrierName = control.get('name')?.value;
            const quantity = control.get('quantity')?.value || 0;
            articleData[carrierName] = quantity;
        });

        storedData[this.checkinKey][articleId] = articleData;
        localStorage.setItem(this.storageKey, JSON.stringify(storedData));
    }

    getCarrierTotal(carrierName: string): number {
        return this.carrierTotals.get(carrierName) || 0;
    }

    get carriers() {
        return this.form.get('carriers') as FormArray;
    }

    private addCarrierForm(carrier: LoadCarrier) {
        const carrierForm = this.fb.group({
            _key: [carrier._key],
            name: [carrier.name],
            description: [carrier.description],
            quantity: [{ value: 0, disabled: !this.articleId() }],
            damaged: [carrier.damaged],
            notice: [carrier.notice],
        });

        this.carriers.push(carrierForm);
    }

    private getUniqueCarriers(): LoadCarrier[] {
        if (!this.deliveryNotes?.length) return [];

        const uniqueCarriers = new Map<string, LoadCarrier>();

        // First, collect all carriers from delivery notes
        this.deliveryNotes.forEach(note => {
            note.loadCarriersConsignee?.forEach(carrier => {
                if (!uniqueCarriers.has(carrier.name)) {
                    const combinedCarrier: LoadCarrier = {
                        _key: carrier._key,
                        name: carrier.name,
                        description: carrier.description,
                        damaged: carrier.damaged || false,
                        notice: carrier.notice || '',
                        quantity: 0,
                    };
                    uniqueCarriers.set(carrier.name, combinedCarrier);
                } else {
                    const existingCarrier = uniqueCarriers.get(carrier.name)!;
                    existingCarrier.damaged =
                        existingCarrier.damaged || carrier.damaged;
                    if (
                        carrier.notice &&
                        !existingCarrier.notice.includes(carrier.notice)
                    ) {
                        existingCarrier.notice =
                            `${existingCarrier.notice} ${carrier.notice}`.trim();
                    }
                }
            });
        });

        // Filter out carriers with total = 0
        const carriersToShow = Array.from(uniqueCarriers.values()).filter(
            carrier => {
                const total = this.getCarrierTotal(carrier.name);
                return total > 0;
            }
        );

        return carriersToShow;
    }

    onSubmit() {
        if (this.form.valid) {
            this.saveQuantities();
            console.log(this.form.value);
        }
    }

    getStoredTotalForCarrier(carrierName: string): number {
        const saved = localStorage.getItem(this.storageKey);
        if (!saved || !this.checkinKey) return 0;

        const storedData: StoredCarrierData = JSON.parse(saved);
        const checkinData = storedData[this.checkinKey] || {};
        let total = 0;

        Object.values(checkinData).forEach(articleData => {
            total += articleData[carrierName] || 0;
        });

        return total;
    }

    getCurrentArticleQuantity(carrierName: string): number {
        const articleId = this.articleId();
        if (!articleId) return 0;

        const saved = localStorage.getItem(this.storageKey);
        if (!saved || !this.checkinKey) return 0;

        const storedData: StoredCarrierData = JSON.parse(saved);
        return storedData[this.checkinKey]?.[articleId]?.[carrierName] || 0;
    }

    static clearCarrierDataForCheckin(checkinKey: string): void {
        const storageKey = 'carrierQuantities';
        const saved = localStorage.getItem(storageKey);
        if (saved) {
            const storedData: StoredCarrierData = JSON.parse(saved);
            delete storedData[checkinKey];
            localStorage.setItem(storageKey, JSON.stringify(storedData));
        }
    }

    showCustomTooltip(carrierName: string, element: HTMLElement) {
        if (this.overlayRef) {
            this.overlayRef.dispose();
        }

        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo(element)
            .withPositions([
                {
                    originX: 'end',
                    originY: 'center',
                    overlayX: 'start',
                    overlayY: 'center',
                    offsetX: 30,
                },
                {
                    originX: 'start',
                    originY: 'bottom',
                    overlayX: 'start',
                    overlayY: 'top',
                    offsetY: 8,
                },
                {
                    originX: 'start',
                    originY: 'top',
                    overlayX: 'start',
                    overlayY: 'bottom',
                    offsetY: -8,
                },
            ])
            .withDefaultOffsetX(8)
            .withDefaultOffsetY(0);

        this.overlayRef = this.overlay.create({
            positionStrategy,
            scrollStrategy: this.overlay.scrollStrategies.reposition(),
        });

        const tooltipPortal = new ComponentPortal(CustomTooltipComponent);
        const tooltipRef = this.overlayRef.attach(tooltipPortal);

        const content = `${this.translateService.instant('INCOMING-GOODS.LOAD-CARRIER-FORM.TOOLTIP.FROM-ALL-ARTICLES')}: ${this.getStoredTotalForCarrier(carrierName)} </br>
        ${this.translateService.instant('INCOMING-GOODS.LOAD-CARRIER-FORM.TOOLTIP.TOTAL-RECEIVED')}: ${this.getCarrierTotal(carrierName)}`;

        tooltipRef.setInput('content', content);
    }

    closeCustomTooltip() {
        if (this.overlayRef) {
            this.overlayRef.dispose();
        }
    }
}
